import { useState, type ReactNode } from "react";
import { Check, ChevronDown } from "lucide-react";
import { SUPPORTED_LANGUAGES } from "@repo/api-schema";

/**
 * Narrow language-code union taken from the const list rather than the
 * package's exported `SupportedLanguageCode` (which widens to `string`). This
 * matches the generated client's narrow `Group["language"]`, so the picker
 * stays assignable to the create/update group request and group settings.
 */
export type LanguageCode = (typeof SUPPORTED_LANGUAGES)[number]["code"];

// Code -> entry lookup, built once at module load. Avoids the O(n) `find`
// scan on every render of every combobox instance.
const LANGUAGE_BY_CODE: ReadonlyMap<
  LanguageCode,
  (typeof SUPPORTED_LANGUAGES)[number]
> = new Map(SUPPORTED_LANGUAGES.map((l) => [l.code, l]));
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { cn } from "../lib/utils";

interface LanguageComboboxProps {
  id?: string;
  value: LanguageCode;
  onChange: (code: LanguageCode) => void;
  disabled?: boolean;
}

/**
 * Language picker for a group's scanned-receipt translation target (issue
 * #284). Mirrors {@link CurrencyCombobox}'s searchable popover so the two
 * settings controls feel identical; the only shape difference is that a
 * language entry has just a `name` (endonym + English) and no short code to
 * show alongside it.
 */
export function LanguageCombobox({
  id,
  value,
  onChange,
  disabled,
}: LanguageComboboxProps): ReactNode {
  const [open, setOpen] = useState(false);
  const selected = LANGUAGE_BY_CODE.get(value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        id={id}
        type="button"
        role="combobox"
        aria-expanded={open}
        disabled={disabled}
        className={cn(
          "flex h-11 w-full cursor-pointer items-center justify-between rounded-sm bg-surface-container-low px-4 text-body-md text-on-surface transition-colors hover:bg-surface-container focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50",
        )}
      >
        <span className="flex items-center gap-2 truncate">
          {selected ? (
            <span className="truncate">{selected.name}</span>
          ) : (
            <span className="text-on-surface-variant">Select language…</span>
          )}
        </span>
        <ChevronDown className="ml-2 size-4 shrink-0 text-on-surface-variant" />
      </PopoverTrigger>
      <PopoverContent className="p-0">
        <Command
          filter={(itemValue, search) => {
            const needle = search.trim().toLowerCase();
            if (!needle) return 1;
            return itemValue.toLowerCase().includes(needle) ? 1 : 0;
          }}
        >
          <CommandInput placeholder="Search language…" />
          <CommandList>
            <CommandEmpty>No language found.</CommandEmpty>
            <CommandGroup>
              {SUPPORTED_LANGUAGES.map((l) => (
                <CommandItem
                  key={l.code}
                  value={`${l.code} ${l.name}`}
                  onSelect={() => {
                    onChange(l.code);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "size-4",
                      value === l.code ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <span>{l.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
